/*
 * @Description: Hero section for AI Video Editor - Watch Your Words Shape the Video
 */
import Image from 'next/image';
import React from 'react';

import StartButton from './StartButton';

const CDN_BASE = 'https://assets.falcocut.ai/ai-video-editor';

interface ShowcaseCard {
  imageUrl: string;
  alt: string;
  title: string;
  description: string;
}

const showcaseCards: ShowcaseCard[] = [
  {
    imageUrl: `${CDN_BASE}/tiktok-style-ad.png`,
    alt: 'Style TikTok Ads with AI video editor',
    title: 'Style TikTok Ads',
    description:
      'Transform raw clips into viral-ready assets by applying trendy visual aesthetics and fast-paced text overlays that stop the scroll.',
  },
  {
    imageUrl: `${CDN_BASE}/product-showcase.png`,
    alt: 'Render Product Showcases with AI video editor',
    title: 'Render Product Showcases',
    description:
      'Transport your items into premium scenes with professional lighting and background swaps that make any product look luxury.',
  },
  {
    imageUrl: `${CDN_BASE}/marketing-video.png`,
    alt: 'Refine Marketing Videos with AI video editor',
    title: 'Refine Marketing Videos',
    description:
      'Polish standard footage into high-energy promotional clips using bold styling and dynamic text to drive immediate clicks.',
  },
];

const HeroSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Watch Your Words Shape the Video
        </h2>

        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-5xl mx-auto mb-8 pad:mb-10 sm:mb-12 leading-relaxed">
          See how easy editing becomes when everything starts with text in FalcoCut. From
          changing the visual style to applying the final polish, you can refine your
          videos naturally without touching a complex timeline.
        </p>

        {/* Showcase Cards */}
        <div className="grid grid-cols-1 pad:grid-cols-3 gap-6 pad:gap-8 mb-10 pad:mb-12 sm:mb-14">
          {showcaseCards.map((card, index) => (
            <div key={index} className="flex flex-col">
              {/* Card Image */}
              <div className="relative rounded-2xl overflow-hidden bg-gray-100 aspect-[4/3] mb-4 pad:mb-5">
                <Image
                  src={card.imageUrl}
                  alt={card.alt}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 33vw, 380px"
                />
              </div>
              {/* Card Text */}
              <h3 className="text-lg pad:text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                {card.title}
              </h3>
              <p className="text-sm pad:text-base text-gray-600 leading-relaxed">
                {card.description}
              </p>
            </div>
          ))}
        </div>

        <div className="flex justify-center">
          <StartButton text="Start Editing Now" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
